import React, { useEffect, useState, useRef } from 'react';
import { X, Maximize2, PanelRightClose, Send, Mic, Trash2, Bot, Sparkles } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import { ChatMessage } from './ChatMessage';
type ViewMode = 'panel' | 'fullscreen';
interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ChatPanel({
  isOpen,
  onClose
}: ChatPanelProps) {
  const {
    messages,
    sendMessage,
    isLoading,
    suggestedPrompts,
    clearMessages
  } = useChat();
  const [input, setInput] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('panel');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      // Reset to panel mode when closed so next open starts as panel
      setViewMode('panel');
    }
  }, [isOpen]);
  // Escape key closes or collapses
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (viewMode === 'fullscreen') {
          setViewMode('panel');
        } else {
          onClose();
        }
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, viewMode, onClose]);
  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const showSuggestions = messages.length <= 1;
  const isFullscreen = viewMode === 'fullscreen';
  return <>
      {/* Backdrop for panel mode — click outside to close */}
      {isOpen && !isFullscreen && <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" data-id="element-44" />}

      {/* Panel / Fullscreen container */}
      <div className={`fixed z-50 flex flex-col transition-all duration-300 ease-out ${isFullscreen ? 'inset-0 bg-white dark:bg-dark-bg' : `right-0 top-0 h-full w-[400px] bg-white dark:bg-dark-surface border-l border-gray-200 dark:border-dark-border shadow-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}`} style={!isFullscreen ? {
      maxWidth: '400px'
    } : undefined} data-id="element-45">
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-dark-border shrink-0 ${isFullscreen ? 'bg-white dark:bg-dark-bg' : 'bg-white dark:bg-dark-surface'}`} data-id="element-46">
          <div className="flex items-center gap-2.5" data-id="element-47">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
            background: '#0577FF'
          }} data-id="element-48">
              <Bot size={16} className="text-white" data-id="element-49" />
            </div>
            <div data-id="element-50">
              <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-51">
                Possible Assistant
              </p>
              <div className="flex items-center gap-1" data-id="element-52">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" data-id="element-53" />
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-54">
                  Online · Powered by AI
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1" data-id="element-55">
            <button onClick={clearMessages} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Clear chat" data-id="element-56">
              <Trash2 size={15} data-id="element-57" />
            </button>

            {isFullscreen ? <button onClick={() => setViewMode('panel')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Collapse to side panel" data-id="element-58">
                <PanelRightClose size={15} data-id="element-59" />
              </button> : <button onClick={() => setViewMode('fullscreen')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Expand to full screen" data-id="element-60">
                <Maximize2 size={15} data-id="element-61" />
              </button>}

            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Close" data-id="element-62">
              <X size={15} data-id="element-63" />
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div className={`flex-1 overflow-y-auto p-4 ${isFullscreen ? 'px-6' : ''}`} data-id="element-64">
          <div className={isFullscreen ? 'max-w-2xl mx-auto w-full' : ''} data-id="element-65">
            {messages.map(msg => <ChatMessage key={msg.id} message={msg} data-id="element-66" />)}

            {/* Loading indicator */}
            {isLoading && <div className="flex gap-2.5 mb-4" data-id="element-67">
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{
              background: '#0577FF'
            }} data-id="element-68">
                  <Bot size={14} className="text-white" data-id="element-69" />
                </div>
                <div className="bg-white dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm" data-id="element-70">
                  <div className="flex gap-1 items-center h-4" data-id="element-71">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-72" />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-73" />
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-74" />
                  </div>
                </div>
              </div>}

            {/* Suggested prompts */}
            {showSuggestions && !isLoading && <div className="mt-2" data-id="element-75">
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1" data-id="element-76">
                  <Sparkles size={11} data-id="element-77" /> Try asking...
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-78">
                  {suggestedPrompts.slice(0, isFullscreen ? 6 : 4).map((prompt, i) => <button key={i} onClick={() => sendMessage(prompt)} className="text-xs bg-gray-100 dark:bg-dark-hover hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 text-gray-600 dark:text-gray-400 px-3 py-1.5 rounded-full transition-colors border border-gray-200 dark:border-dark-border" data-id="element-79">
                        {prompt}
                      </button>)}
                </div>
              </div>}

            <div ref={messagesEndRef} data-id="element-80" />
          </div>
        </div>

        {/* Input area */}
        <div className={`shrink-0 p-3 border-t border-gray-200 dark:border-dark-border ${isFullscreen ? 'bg-white dark:bg-dark-bg' : 'bg-white dark:bg-dark-surface'}`} data-id="element-81">
          <div className={isFullscreen ? 'max-w-2xl mx-auto w-full' : ''} data-id="element-82">
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-dark-hover rounded-xl border border-gray-200 dark:border-dark-border px-3 py-2 focus-within:border-primary-400 dark:focus-within:border-primary-500 transition-colors" data-id="element-83">
              <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about your finances..." className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none" disabled={isLoading} data-id="element-84" />
              <button className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Voice input" data-id="element-85">
                <Mic size={16} data-id="element-86" />
              </button>
              <button onClick={handleSend} disabled={!input.trim() || isLoading} className="w-7 h-7 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors" data-id="element-87">
                <Send size={13} className="text-white" data-id="element-88" />
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2" data-id="element-89">
              AI responses are for informational purposes only
            </p>
          </div>
        </div>
      </div>
    </>;
}